import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  isPermissionRefused,
  type TrackingMatch,
  type TrackingSearchResponse,
} from '@citibox-panel/api-client';
import { detectTracking } from '../lib/detectTracking';

export type TrackingSearchStatus = 'idle' | 'searching';

// The outcomes, as a discriminated union rather than as data a caller reads flags off. That
// shape is the requirement, not a style choice: `error` and `none` have to be impossible to
// conflate, and a union has no falsy value they share. A read that was REJECTED is not a tracking
// that does not exist — the endpoint answers 404 to everything today, so blurring the two would
// tell an agent holding a parcel that it was never stored.
export type TrackingSearchOutcome =
  | { result: 'single'; match: TrackingMatch }
  // `count` travels beside the rows because they are different numbers: the server caps the list
  // and counts the window, so this is what lets a caller say «truncated» instead of miscounting.
  | { result: 'multiple'; matches: TrackingMatch[]; count: number }
  | { result: 'none' }
  | { result: 'error' }
  // A permission refusal is set apart from `error` because it is not this caller's to report: the
  // host answers it by replacing the whole view, so a notice would only flash over that screen.
  | { result: 'refused' };

export interface UseTrackingSearch {
  // Null when there was nothing to search — the term is below the floor. Not an outcome: nothing
  // was asked, so there is nothing to report.
  submit: (rawInput: string) => Promise<TrackingSearchOutcome | null>;
  status: TrackingSearchStatus;
}

const FIVE_MINUTES = 5 * 60 * 1000;

const outcomeOf = ({ count, results }: TrackingSearchResponse): TrackingSearchOutcome => {
  const [first] = results;
  if (first === undefined) return { result: 'none' };
  if (results.length === 1) return { result: 'single', match: first };
  return { result: 'multiple', matches: results, count };
};

// Search-on-submit, never typeahead: a tracking is pasted whole, and a read per keystroke would be
// a read per keystroke against an exact match that cannot succeed until the last character.
//
// `submit` resolves the outcome and returns it, so the caller acts once and synchronously with the
// resolution — cache included, since `fetchQuery` returns a fresh entry without refetching and
// there is no loading→resolved transition to observe. Read-only: nothing invalidates this key, and
// no detail mutation does either — the modal renders no `status`, so there is no field that can go
// stale while it is on screen.
export function useTrackingSearch(
  search: (tracking: string) => Promise<TrackingSearchResponse>,
): UseTrackingSearch {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TrackingSearchStatus>('idle');

  const submit = useCallback(
    async (rawInput: string): Promise<TrackingSearchOutcome | null> => {
      const tracking = detectTracking(rawInput);
      if (tracking === null) return null;

      setStatus('searching');
      try {
        const response = await queryClient.fetchQuery({
          queryKey: ['tracking-search', tracking],
          queryFn: () => search(tracking),
          staleTime: FIVE_MINUTES,
        });
        return outcomeOf(response);
      } catch (error) {
        // Swallowed and REPORTED, unlike the phone search's rethrow: there the caller catches to
        // paint a recoverable row, here the failure is one of the outcomes the caller switches on.
        // One shape for every ending is what keeps «error» from being handled as an afterthought.
        return isPermissionRefused(error) ? { result: 'refused' } : { result: 'error' };
      } finally {
        // In `finally`, so a rejection cannot leave the loading row on screen for ever.
        setStatus('idle');
      }
    },
    [queryClient, search],
  );

  return { submit, status };
}
